import { Button } from '@/components/ui/button'
import { ErrorState } from '@/components/ui/error-state'
import type { ErrorComponentProps } from '@tanstack/react-router'
import { AlertTriangle } from 'lucide-react'
import { Component, type ReactNode } from 'react'

/**
 * What a route renders when it throws. The raw message is a stack trace detail, so it stays in dev
 * builds and production users get the support line instead.
 */
export function RouteErrorState({ error, reset }: ErrorComponentProps) {
	const message = import.meta.env.DEV && error instanceof Error ? error.message : undefined

	return (
		<ErrorState
			title='Something went wrong'
			icon={AlertTriangle}
			tone='destructive'
			message={message}
			showSupportEmail={!message}
			actions={
				<Button variant='outline' size='sm' onClick={reset} data-testid='route-error-retry'>
					Try again
				</Button>
			}
		/>
	)
}

interface AppErrorBoundaryState {
	error: unknown
	failed: boolean
}

/**
 * Last resort for a render crash outside any route, where the router's own boundary cannot help.
 * Reporting happens in the root's onCaughtError handler, the same place every boundary reports.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, AppErrorBoundaryState> {
	state: AppErrorBoundaryState = { error: null, failed: false }

	static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
		return { error, failed: true }
	}

	render() {
		if (this.state.failed) {
			return (
				<AppErrorFallback error={this.state.error} resetError={() => this.setState({ error: null, failed: false })} />
			)
		}
		return this.props.children
	}
}

function AppErrorFallback({ error, resetError }: { error: unknown; resetError: () => void }) {
	const message = import.meta.env.DEV && error instanceof Error ? error.message : undefined

	return (
		<ErrorState
			title='Something went wrong'
			icon={AlertTriangle}
			tone='destructive'
			message={message}
			showSupportEmail={!message}
			containerClassName='min-h-screen min-w-screen bg-background overflow-hidden flex items-center justify-center'
			actions={
				<Button variant='outline' size='sm' onClick={resetError} data-testid='app-error-retry'>
					Try again
				</Button>
			}
		/>
	)
}
